import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiClock, FiMapPin, FiMusic, FiBookOpen, FiUsers, FiSun } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiSun,
      title: 'Sunday Worship',
      time: '10:00 AM',
      description: 'Join us for inspiring worship, powerful preaching, and fellowship with our church family.',
      features: ['Contemporary Worship', 'Biblical Teaching', 'Prayer Time', 'Community Fellowship']
    },
    {
      icon: FiMusic,
      title: 'Praise & Worship',
      time: '9:30 AM',
      description: 'Experience the joy of worship through music and song before our main service.',
      features: ['Live Band', 'Contemporary Songs', 'Traditional Hymns', 'Spirit-led Worship']
    },
    {
      icon: FiBookOpen,
      title: 'Bible Study',
      time: 'Wednesday 7:00 PM',
      description: 'Dive deeper into God\'s Word with our interactive Bible study sessions.',
      features: ['Verse by Verse Study', 'Group Discussion', 'Prayer Support', 'Spiritual Growth']
    },
    {
      icon: FiUsers,
      title: 'Youth Ministry',
      time: 'Friday 6:00 PM',
      description: 'Engaging programs designed specifically for teenagers and young adults.',
      features: ['Youth Games', 'Biblical Teaching', 'Mentorship', 'Community Service']
    }
  ];

  return (
    <section id="services" className="py-20 bg-white dark:bg-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Our Weekly
            <span className="block text-blue-600 dark:text-blue-400">Services & Programs</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Join us throughout the week for worship, learning, and fellowship. 
            Everyone is welcome regardless of where you are in your faith journey.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 dark:bg-gray-700 rounded-xl p-8 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-blue-600 dark:bg-blue-500 rounded-lg flex items-center justify-center group-hover:bg-blue-700 dark:group-hover:bg-blue-600 transition-colors duration-300">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{service.title}</h3>
                    <span className="bg-yellow-400 dark:bg-yellow-300 text-black px-3 py-1 rounded-full text-sm font-semibold">
                      {service.time}
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-gray-700 dark:text-gray-300">
                        <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Location & Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 rounded-xl p-8 text-white"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-4">Visit Us This Sunday</h3>
              <p className="text-blue-100 dark:text-blue-50 mb-6">
                We would love to have you join our church family. Come as you are and experience 
                the love and grace of our community.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 mr-3 text-yellow-400 dark:text-yellow-300" />
                  <span>14 Ada-George Road, Port-Harcourt City, Rivers State, Nigeria</span>
                </div>
                <div className="flex items-center">
                  <SafeIcon icon={FiClock} className="w-5 h-5 mr-3 text-yellow-400 dark:text-yellow-300" />
                  <span>Sunday Service: 10:00 AM</span>
                </div>
              </div>
            </div>
            <div className="text-center">
              <button className="bg-yellow-400 hover:bg-yellow-500 dark:bg-yellow-300 dark:hover:bg-yellow-400 text-black font-semibold px-8 py-4 rounded-lg transition-colors duration-300">
                Plan Your Visit
              </button>
              <p className="text-blue-100 dark:text-blue-50 mt-4">
                First time visitor? We have a special welcome for you!
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;